import { MdClose, MdKeyboardArrowUp } from 'react-icons/md'

import Button from '../common/Button/Button.component'
import DemographicsForm from './DemographicsForm'
import FormUtils from '../../utils/FormUtils'
import { IconButton } from '@mui/material'
import React from 'react'
import customerStore from '../../datastore/CustomerStore'
import { keyframes } from '@emotion/react'
import { observer } from 'mobx-react'
import styled from '@emotion/styled'
import theme from '../../global/theme'
import { upsertPreLeadObject } from '../../service/preLeadService'

let barMinimizedByUser = false
const ONBOARDING_KEY = 'demographics_bar_onboarded'
const CONFIRMED_KEY = 'demographics_bar_confirmed'

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`

const FixedContainer = styled.div`
  position: fixed;
  bottom: 16px;
  right: 60px;
  z-index: 999;
  width: 500px;

  @media screen and (max-width: 600px) {
    right: unset;
    left: 10px;
    width: calc(100% - 20px);
  }
`

const ExpandedBar = styled.div`
  background: ${theme.colors.white};
  border-radius: 12px;
  border: 1px solid ${theme.colors.contrastSecondary};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14);
  padding: 20px 40px;
  display: flex;
  gap: 10px;
  animation: ${slideUp} 0.3s ease;
  flex-direction: column;
  align-items: stretch;

  @media screen and (max-width: 600px) {
    padding: 14px 16px;
  }
`

const MinimizedChip = styled.button`
  position: fixed;
  bottom: 30px;
  right: 60px;
  z-index: 999;

  @media screen and (max-width: 600px) {
    right: unset;
    left: 10px;
  }

  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: ${theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition:
    transform 0.15s,
    box-shadow 0.15s;
  animation: ${slideUp} 0.3s ease;
  letter-spacing: 0.3px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  }
`

const ChipLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  opacity: 0.75;
  margin-right: 2px;
`

const ChipDivider = styled.span`
  opacity: 0.4;
  margin: 0 2px;
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 998;
  animation: ${fadeIn} 0.25s ease;
`

const Callout = styled.div`
  position: absolute;
  bottom: calc(100% + 12px);
  right: 0;
  z-index: 1000;
  background: ${theme.colors.grey};
  border-radius: 16px;
  padding: 16px 20px;
  max-width: 200px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  animation: ${fadeIn} 0.3s ease;

  p {
    font-size: 14px;
    color: ${theme.colors.white};
    font-weight: 500;
    line-height: 1.5;
    margin: 0 0 14px;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: -11.5px;
    right: 30px;
    border: 6px solid transparent;
    border-top-color: ${theme.colors.grey};
    z-index: 1;
  }

  @media screen and (max-width: 600px) {
    right: unset;
    left: 0;

    &::before {
      right: unset;
      left: 30px;
    }
  }
`

const DemographicsConfirmationBar: React.FC = () => {
  const isQuotesProcessing =
    customerStore.isMAQuotesProcessing ||
    customerStore.isMSQuotesProcessing ||
    customerStore.isMPartDQuotesProcessing

  const [isMinimized, setIsMinimized] = React.useState(
    () => barMinimizedByUser || localStorage.getItem(CONFIRMED_KEY) === 'true',
  )
  const [showOnboarding, setShowOnboarding] = React.useState(
    () => !barMinimizedByUser && !localStorage.getItem(ONBOARDING_KEY),
  )

  const dismissOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true')
    setShowOnboarding(false)
  }

  if (isQuotesProcessing) return null

  const chipLabel = [
    customerStore.postalCode,
    customerStore.dateOfBirth
      ? String(FormUtils.getAge(customerStore.dateOfBirth))
      : '',
    customerStore.gender
      ? customerStore.gender.charAt(0).toUpperCase() +
        customerStore.gender.slice(1)
      : '',
  ]
    .filter(Boolean)
    .join(' · ')

  if (isMinimized) {
    return (
      <MinimizedChip
        onClick={() => {
          barMinimizedByUser = false
          setIsMinimized(false)
        }}
        aria-label='Expand demographics bar'
      >
        <MdKeyboardArrowUp size={16} />
        <ChipLabel>Your Info</ChipLabel>
        {chipLabel && (
          <>
            <ChipDivider>|</ChipDivider>
            <span>{chipLabel}</span>
          </>
        )}
      </MinimizedChip>
    )
  }

  const handleSuccess = () => {
    upsertPreLeadObject({
      postalCode: customerStore.postalCode,
      gender: customerStore.gender,
      dob: customerStore.dateOfBirth,
      firstName: customerStore.firstName,
      lastName: customerStore.lastName,
      email: customerStore.email,
      phone: customerStore.phone,
    })
    localStorage.setItem(CONFIRMED_KEY, 'true')
    barMinimizedByUser = true
    setIsMinimized(true)
    dismissOnboarding()
  }

  return (
    <>
      {showOnboarding && <Overlay onClick={dismissOnboarding} />}
      <FixedContainer>
        {showOnboarding && (
          <Callout>
            <p>
              Please ensure your information is accurate to receive the most
              relevant quotes.
            </p>
            <Button
              variant='contained'
              color='secondary'
              bold={false}
              fontSize='13px'
              height='36px'
              width='100%'
              onClick={dismissOnboarding}
              description='Dismiss onboarding callout'
            >
              Got it
            </Button>
          </Callout>
        )}
        <ExpandedBar>
          <DemographicsForm
            heading='Confirm your details'
            subheaderText='Please ensure your information is accurate to receive the most relevant quotes.'
            onSuccess={handleSuccess}
            submitId='updatePlansConfirmationBar'
            extraActions={
              <IconButton
                onClick={() => {
                  barMinimizedByUser = true
                  setIsMinimized(true)
                  dismissOnboarding()
                }}
                size='medium'
                aria-label='Minimize demographics bar'
                sx={{
                  background: theme.colors.secondary,
                  color: '#fff',
                  flexShrink: 0,
                  '&:hover': {
                    background: theme.colors.secondary,
                    opacity: 0.85,
                  },
                }}
              >
                <MdClose size={16} />
              </IconButton>
            }
          />
        </ExpandedBar>
      </FixedContainer>
    </>
  )
}

export default observer(DemographicsConfirmationBar)
